import * as React from 'react'
import { cn } from '../../../lib/utils'

export type InfiniteScrollProps = {
  id?: string
  className?: string
  children?: React.ReactNode
  style?: React.CSSProperties
  scrollContainer: HTMLElement | null | undefined
  direction: 'up' | 'down'
  handleLoadMoreItems: () => void
  threshold?: number
  isLoading?: boolean
}

const InfiniteScroll = React.forwardRef<HTMLDivElement, InfiniteScrollProps>(
  (
    {
      id,
      className,
      children,
      style,
      scrollContainer,
      direction,
      handleLoadMoreItems,
      threshold = 1,
      isLoading = false,
    },
    ref,
  ) => {
    const hasPendingRestoreRef = React.useRef(false)
    const previousScrollTopRef = React.useRef(0)
    const previousScrollHeightRef = React.useRef(0)
    const loadMoreRef = React.useRef(handleLoadMoreItems)
    const isLoadingRef = React.useRef(isLoading)
    const topSentinelRef = React.useRef<HTMLDivElement | null>(null)
    const bottomSentinelRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
      loadMoreRef.current = handleLoadMoreItems
    }, [handleLoadMoreItems])

    React.useEffect(() => {
      isLoadingRef.current = isLoading
    }, [isLoading])

    React.useEffect(() => {
      if (!scrollContainer) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (isLoadingRef.current) return
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            const isBottom = entry.target === bottomSentinelRef.current
            const isTop = entry.target === topSentinelRef.current
            if (
              (direction === 'down' && isBottom) ||
              (direction === 'up' && isTop)
            ) {
              previousScrollTopRef.current = scrollContainer.scrollTop
              previousScrollHeightRef.current = scrollContainer.scrollHeight
              hasPendingRestoreRef.current = true
              loadMoreRef.current()
            }
          }
        },
        {
          root: scrollContainer,
          // Trigger earlier based on threshold pixels
          rootMargin:
            direction === 'down'
              ? `0px 0px ${String(threshold)}px 0px`
              : `${String(threshold)}px 0px 0px 0px`,
          threshold: 0,
        },
      )

      const top = topSentinelRef.current
      const bottom = bottomSentinelRef.current
      if (direction === 'down' && bottom) observer.observe(bottom)
      if (direction === 'up' && top) observer.observe(top)

      return () => {
        observer.disconnect()
      }
    }, [scrollContainer, direction, threshold])

    React.useLayoutEffect(() => {
      if (!scrollContainer) return
      if (!hasPendingRestoreRef.current) return

      const previousScrollHeight = previousScrollHeightRef.current
      const previousScrollTop = previousScrollTopRef.current
      const currentScrollHeight = scrollContainer.scrollHeight

      if (currentScrollHeight !== previousScrollHeight) {
        if (direction === 'up') {
          // For 'up' direction, items are prepended at the top, so we need to
          // adjust scroll position to keep the same content in view
          const addedHeight = currentScrollHeight - previousScrollHeight
          scrollContainer.scrollTop = previousScrollTop + addedHeight
        }
        // For 'down' direction, items are appended at the bottom, so no scroll
        // adjustment is needed - the user's view of existing content is unchanged
        hasPendingRestoreRef.current = false
      }
    }, [children, isLoading, scrollContainer, direction])

    return (
      <div
        ref={ref}
        className={cn('flex flex-col w-full gap-1 col-span-12 grow', className)}
        title={`${id}-infinite-scroll`}
        style={style}
      >
        {direction === 'up' && (
          <div ref={topSentinelRef} style={{ height: 1, width: '100%' }} />
        )}
        {children}
        {direction === 'down' && (
          <div ref={bottomSentinelRef} style={{ height: 1, width: '100%' }} />
        )}
      </div>
    )
  },
)

InfiniteScroll.displayName = 'InfiniteScroll'

export { InfiniteScroll }
